'use client';

import type {
  ChartConfig,
  ProjectionVariant,
  YearProjection,
} from '@polyseme/design-system';

import type { ChartSummaryTooltipLabels } from './chart-summary-tooltip';

import { ChartContainer, ChartTooltip } from '@polyseme/design-system';
import { cn } from '@polyseme/utils/cn/cn-utils';
import { formatCurrency } from '@polyseme/utils/number/number-utils';
import { cva } from 'class-variance-authority';
import { memo, useRef, useState } from 'react';
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from 'recharts';

import { ChartSummaryTooltip } from './chart-summary-tooltip';

const chartKeys = {
  optimistic: 'optimistic',
  median: 'median',
  pessimistic: 'pessimistic',
  investmentMade: 'investmentMade',
} as const;

type ChartAreaAxesProps = {
  axisClassNames?: string;
  blurAmounts?: boolean;
  chartConfig: ChartConfig;
  chartData: YearProjection[];
  /** Override the chart container sizing. Defaults to the modal-friendly
   * responsive heights; pass e.g. `'w-full h-[260px]'` for compact layouts. */
  chartContainerClassName?: string;
  maxAmount?: number;
  showSummary?: boolean;
  variant: ProjectionVariant;
  tooltipLabels: ChartSummaryTooltipLabels;
  onActiveYearChange?: (year: number) => void;
};

const textVariant = cva('', {
  variants: {
    variant: {
      default: '',
      floating: 'stroke-primary',
    },
  },
});

export const ChartAreaAxes = memo(
  ({
    blurAmounts = false,
    chartConfig,
    chartContainerClassName = 'w-full sm:h-[200px] md:h-[300px] lg:h-[440px]',
    chartData,
    maxAmount,
    onActiveYearChange,
    showSummary = true,
    variant,
    tooltipLabels,
  }: ChartAreaAxesProps) => {
    const [currentYear, setCurrentYear] = useState<number | null>(null);
    const lastEmittedYearRef = useRef<number | null>(null);

    const emitYearChange = (year: number) => {
      setCurrentYear(year);
      if (lastEmittedYearRef.current !== year) {
        lastEmittedYearRef.current = year;
        onActiveYearChange?.(year);
      }
    };

    const handleMouseMove = (data: {
      activeIndex?: number | string | null;
      activeLabel?: number | string;
    }) => {
      const index =
        typeof data?.activeIndex === 'string'
          ? parseInt(data.activeIndex, 10)
          : data?.activeIndex;
      if (typeof index === 'number' && chartData[index]) {
        emitYearChange(chartData[index].year);
      }
    };

    const handleMouseLeave = () => {
      const lastDataPoint = chartData[chartData.length - 1];
      if (lastDataPoint) {
        emitYearChange(lastDataPoint.year);
      }
    };

    const lastChartDataPoint = chartData[chartData.length - 1];
    const currentYearProjection =
      currentYear !== null
        ? chartData.find((data) => data.year === currentYear)
        : lastChartDataPoint;

    return (
      <div>
        {!!showSummary && !!currentYearProjection && (
          <ChartSummaryTooltip
            {...currentYearProjection}
            blurAmounts={blurAmounts}
            isLastYear={currentYearProjection.year === lastChartDataPoint?.year}
            labels={tooltipLabels}
            variant={variant}
          />
        )}
        <ChartContainer
          className={chartContainerClassName}
          config={chartConfig}
        >
          <AreaChart
            data={chartData}
            onMouseLeave={handleMouseLeave}
            onMouseMove={handleMouseMove}
          >
            <XAxis
              axisLine={false}
              dataKey="year"
              tick={({
                payload,
                verticalAnchor: _verticalAnchor,
                visibleTicksCount: _visibleTicksCount,
                tickFormatter: _tickFormatter,
                index: _index,
                ...svgProps
              }) => (
                <text
                  {...svgProps}
                  className={cn(textVariant({ variant }))}
                  textAnchor="middle"
                >
                  {payload.value}
                </text>
              )}
              tickLine={false}
              tickMargin={8}
            />
            {variant === 'default' && (
              <YAxis
                axisLine={false}
                className={cn(blurAmounts && 'select-none blur-sm')}
                domain={['auto', maxAmount || 'auto']}
                tick={({
                  payload,
                  verticalAnchor: _verticalAnchor,
                  visibleTicksCount: _visibleTicksCount,
                  tickFormatter: _tickFormatter,
                  index: _index,
                  ...svgProps
                }) => (
                  <text
                    {...svgProps}
                    className={cn(textVariant({ variant }))}
                    textAnchor="end"
                  >
                    {formatCurrency(payload.value, { isCompact: true })}
                  </text>
                )}
                tickLine={false}
                tickMargin={8}
              />
            )}

            <Area
              dataKey={chartKeys.optimistic}
              fill="var(--color-optimistic)"
              fillOpacity={0.5}
              stroke="var(--color-pessimistic)"
            />
            <Area
              activeDot={{
                r: 4,
                fill: 'var(--color-median)',
                stroke: 'white',
                strokeWidth: 2,
              }}
              dataKey={chartKeys.median}
              fill="var(--color-pessimistic)"
              fillOpacity={0.2}
              stroke="var(--color-median)"
              strokeLinejoin="round"
              strokeWidth={2}
            />
            <Area
              dataKey={chartKeys.pessimistic}
              fill="white"
              fillOpacity={1}
              stroke="var(--color-pessimistic)"
            />
            <Area
              activeDot={{
                r: 4,
                fill: 'var(--color-investmentMade)',
                stroke: 'white',
                strokeWidth: 2,
              }}
              dataKey={chartKeys.investmentMade}
              fill="white"
              fillOpacity={1}
              stroke="var(--color-investmentMade)"
              strokeWidth={2}
            />
            <CartesianGrid vertical horizontal={false} />
            <ChartTooltip
              content={() => null}
              cursor={{ strokeWidth: 1, strokeDasharray: '5 5' }}
            />
          </AreaChart>
        </ChartContainer>
      </div>
    );
  }
);

ChartAreaAxes.displayName = 'ChartAreaAxes';
